const textOrEmpty = (value) => (value === undefined || value === null ? '' : String(value))

const getLocationKey = (trackerLocation) =>
  trackerLocation?.tracker?.sssid || trackerLocation?.sssid || trackerLocation?.lastLoc?.sssid

const getDriverName = (record) => {
  if (record?.status === '0' || record?.status === 0) return ''
  return textOrEmpty(record?.driver_name || record?.driverName)
}

const getCarNumber = (record) =>
  textOrEmpty(
    record?.car?.car_license_plate ||
      record?.car?.carNumber ||
      record?.car_number ||
      record?.carNumber
  )

const getCurrentDriverInfo = (record) => {
  if (!record || record.status === '0' || record.status === 0) return null
  if (!record.driver_id && !record.driver_name && !record.driverName) return null

  return {
    driverId: record.driver_id || record.driverId || '',
    driverName: getDriverName(record),
    driverCode: textOrEmpty(record.driver_code || record.driverCode),
    phone: textOrEmpty(
      record.driver_cellphone || record.driverCellphone || record.driver_phone || record.driverPhone
    ),
    identity: textOrEmpty(record.driver_identity || record.driverIdentity),
    drivingLicense: textOrEmpty(record.driving_license || record.drivingLicense),
    email: textOrEmpty(record.driver_email || record.driverEmail),
    pictureBase64: textOrEmpty(record.driver_picture_base64 || record.driverPictureBase64),
    rfid: textOrEmpty(record.rfid),
    carNumber: getCarNumber(record),
    carName: textOrEmpty(record?.car?.car_name || record?.car?.carName),
    carModel: textOrEmpty(record?.car?.car_model || record?.car?.carModel),
  }
}

/**
 * Keep the last confirmed RFID association while a location refresh is being
 * enriched. The next RFID snapshot remains authoritative and can clear it.
 */
function preserveRealtimeDriverInfo(previousLocation, nextLocation) {
  const previousTracker = previousLocation?.tracker
  if (!nextLocation || (!previousTracker?.driverInfo && !previousTracker?.carNumber)) return nextLocation

  const nextTracker = nextLocation.tracker || {}
  const nextLastLoc = nextLocation.lastLoc || {}
  const driverInfo = previousTracker.driverInfo ? { ...previousTracker.driverInfo } : null
  const driverName = previousTracker.driverName || driverInfo?.driverName || ''
  const carNumber = previousTracker.carNumber || driverInfo?.carNumber || ''

  nextTracker.driverInfo = driverInfo
  nextTracker.driverName = driverName
  nextTracker.carNumber = carNumber
  nextLastLoc.driverName = driverName
  nextLastLoc.carNumber = carNumber
  nextLocation.tracker = nextTracker
  nextLocation.lastLoc = nextLastLoc
  return nextLocation
}

/**
 * Merge the backend's RFID snapshot into location objects used by both the
 * realtime table and map popup. Every requested device is reset first so a
 * logout cannot leave the previous driver's name visible.
 */
function mergeRealtimeDriverInfo(trackerLocations, records) {
  const recordMap = new Map(
    (Array.isArray(records) ? records : [])
      .filter((record) => record && record.ssid)
      .map((record) => [String(record.ssid), record])
  )

  ;(Array.isArray(trackerLocations) ? trackerLocations : []).forEach((trackerLocation) => {
    const key = getLocationKey(trackerLocation)
    if (!key) return

    const tracker = trackerLocation.tracker || {}
    const lastLoc = trackerLocation.lastLoc || {}

    // A successful snapshot is authoritative for every requested device.
    // Reset first so a logout or missing association cannot retain stale data.
    tracker.driverName = ''
    tracker.carNumber = ''
    tracker.driverInfo = null
    lastLoc.driverName = ''
    lastLoc.carNumber = ''

    const record = recordMap.get(String(key))
    if (!record) {
      trackerLocation.tracker = tracker
      trackerLocation.lastLoc = lastLoc
      return
    }

    const driverName = getDriverName(record)
    const carNumber = getCarNumber(record)

    tracker.driverName = driverName
    tracker.carNumber = carNumber
    tracker.driverInfo = getCurrentDriverInfo(record)
    lastLoc.driverName = driverName
    lastLoc.carNumber = carNumber
    trackerLocation.tracker = tracker
    trackerLocation.lastLoc = lastLoc
  })

  return trackerLocations
}

function getRealtimeDriverInfoSssids(trackerLocations, selectedSssids) {
  const selectedSet =
    selectedSssids == null
      ? null
      : new Set(Array.from(selectedSssids, (sssid) => String(sssid)))

  return [...new Set(
    (Array.isArray(trackerLocations) ? trackerLocations : [])
      .map(getLocationKey)
      .filter((sssid) => sssid && (!selectedSet || selectedSet.has(String(sssid))))
  )]
}

module.exports = {
  mergeRealtimeDriverInfo,
  preserveRealtimeDriverInfo,
  getRealtimeDriverInfoSssids,
}
